import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, CheckCircle2, XCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  suspended: 'bg-neutral-200 text-neutral-800',
};

export default function CrmPartners() {
  const { staff, authHeader, API } = useStaffAuth();
  const [partners, setPartners] = useState([]);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const canManage = staff.role !== 'rm';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/crm/partners`, { headers: authHeader(), params }),
        axios.get(`${API}/crm/staff`, { headers: authHeader() }),
      ]);
      setPartners(pRes.data.partners || []);
      setRms((sRes.data.staff || []).filter((s) => s.active));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load partners'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/crm/partners/${id}`, patch, { headers: authHeader() });
      setPartners((prev) => prev.map((p) => (p.id === id ? res.data.partner : p)));
      toast.success('Partner updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Update failed'));
    }
  };

  const rmName = (id) => (id ? rms.find((r) => r.id === id)?.name || '—' : '—');

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Referral partners</h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">
            Approve new partners and assign them a relationship manager.
          </p>
        </div>
        <Button onClick={load} variant="outline"
          className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-6 inline-flex p-1 rounded-full bg-[#0B3B2E]/8">
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatusFilter(t.key)}
            className={`px-4 h-9 rounded-full text-[13px] font-medium transition-all ${
              statusFilter === t.key ? 'bg-[#0B3B2E] text-[#F5F1E8]' : 'text-[#0B3B2E]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E] bg-white rounded-2xl border border-[#0B3B2E]/10">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : partners.length === 0 ? (
          <div className="p-16 text-center text-[#0E1F1A]/55 bg-white rounded-2xl border border-[#0B3B2E]/10">
            No partners in this view.
          </div>
        ) : partners.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-5 lg:p-6">
            <div className="flex flex-wrap items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#0B3B2E]/8 text-[#0B3B2E] flex items-center justify-center">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-serif text-[19px] text-[#0B3B2E]">{p.company}</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[p.status] || 'bg-neutral-100'}`}>
                    {p.status}
                  </span>
                </div>
                <p className="text-[13px] text-[#0E1F1A]/70 mt-0.5">
                  {p.name} · {p.email}{p.phone ? ` · ${p.phone}` : ''}{p.country ? ` · ${p.country}` : ''}
                </p>
                <p className="text-[11px] text-[#0E1F1A]/50 mt-1">
                  Registered {p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'} · RM: {rmName(p.assigned_rm_id)}
                </p>
              </div>

              {canManage ? (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5 border border-[#0B3B2E]/15 rounded-md bg-white h-9 px-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#0E1F1A]/55">Commission</span>
                    <input
                      type="number"
                      min="0" max="100" step="0.5"
                      defaultValue={p.commission_pct ?? 5}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isNaN(v) || v === (p.commission_pct ?? 5)) return;
                        update(p.id, { commission_pct: v });
                      }}
                      className="w-14 h-7 rounded bg-transparent text-[12px] text-right outline-none focus:ring-1 focus:ring-[#0B3B2E]/30"
                    />
                    <span className="text-[11px] text-[#0E1F1A]/60">%</span>
                  </div>
                  <select value={p.assigned_rm_id || ''}
                    onChange={(e) => update(p.id, { assigned_rm_id: e.target.value || null })}
                    className="h-9 rounded-md border border-[#0B3B2E]/20 bg-white px-2 text-[12px]">
                    <option value="">Assign RM…</option>
                    {rms.map((r) => (
                      <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                    ))}
                  </select>
                  {p.status !== 'approved' && (
                    <Button onClick={() => update(p.id, { status: 'approved' })}
                      className="h-9 rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] text-[12px]">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  )}
                  {p.status !== 'rejected' && (
                    <Button onClick={() => update(p.id, { status: 'rejected' })}
                      variant="outline"
                      className="h-9 rounded-full border-[#0B3B2E]/20 bg-transparent text-[#0B3B2E] hover:bg-rose-50 hover:border-rose-300 text-[12px]">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-[12px] text-[#0E1F1A]/55">Read-only</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
